import { useEffect, useMemo, useRef, useState } from 'react';
import { EMITTER_CONSTANTS, KEY_RECOIL, STRIP_EVENT_THUMBNAIL_INFO } from '@/shared/config';
import { useEmitter, usePaginationKeyboard } from '@/shared/lib/customHooks';
import { createOnChangePageString } from '@/shared/lib/parsePageInput';
import {
  RHYTHM_EVENT_TAB_ID,
  selectIsOpenModalEditStrip,
  selectPageIndex,
  useReportStore,
} from '@/entities/report';
import type { BookmarkPaginationContainerProps } from './bookmarkPaginationContainer';

/**
 * Backs the pager under the ECG bookmark strip grid. Derives the page count from the bookmark total against the per-page strip limit, exposes previous and next guards, validates a typed page number on Enter and on blur before committing a new page index, and binds the Z and X shortcuts while the rhythm-event tab is hovered.
 *
 * @param props - Pagination inputs; isDisabled suppresses the keyboard shortcuts.
 * @returns An object carrying isShowPagination, isDisabledPrev, isDisabledNext, pageString, inputSize, isError, maxPageNumber and the onClickPrevious, onClickNext, onChangePageString, onKeyDown and onBlur handlers.
 */
const useBookmarkPaginationContainer = ({ isDisabled }: BookmarkPaginationContainerProps) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;

  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Stores which page of bookmark strips the grid shows. Forwards the value to the store action with the tab-2 key already bound, so callers pass only the new index.
   *
   * @param v - The new page index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const sizeThumbnailConfig = useReportStore((s) => s.sizeEventThumbnailConfig);
  const reloadViewSizeConfig = useReportStore((s) => s.reloadViewSizeConfig);
  const ecgBookmarks = useReportStore((s) => s.ecgBookmarks);
  const isOpenModalEditStrip = useReportStore(selectIsOpenModalEditStrip(keyRecoil));

  const [pageString, setPageString] = useState(pageIndex.index + 1);
  const [inputSize, setInputSize] = useState(String(pageIndex.index + 1).length + 1);
  const [isError, setIsError] = useState(false);
  const [isOpenModaltrip, setIsOpenModalStrip] = useState(false);

  const pageStringRef = useRef(pageString);
  const maxPageNumberRef = useRef<number>();

  const totalStrip = ecgBookmarks.length;

  const maxPageNumber = useMemo(
    () => (totalStrip ? Math.ceil(totalStrip / STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit) : 1),
    [
      totalStrip,
      sizeThumbnailConfig,
      STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit,
      reloadViewSizeConfig,
    ],
  );
  maxPageNumberRef.current = maxPageNumber;

  const isShowPagination = true;
  const isDisabledNext =
    (pageIndex.index + 1) * STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit >= totalStrip;
  const isDisabledPrev = pageIndex.index === 0;

  /**
   * Moves the bookmark strip grid back one page. Writes the current page index minus one into the tab-2 store; the caller guards the lower bound.
   *
   * @returns Nothing.
   */
  const onClickPrevious = () => {
    setPageIndex({ index: pageIndex.index - 1 });
  };

  /**
   * Advances the bookmark strip grid to the following page. Writes the current page index plus one into the tab-2 store.
   *
   * @returns Nothing.
   */
  const onClickNext = () => {
    setPageIndex({ index: pageIndex.index + 1 });
  };

  const onChangePageString = createOnChangePageString({
    setIsError,
    setPageString,
    pageStringRef,
    setInputSize,
  });

  /**
   * Commits the typed page number when the pagination input loses focus. Accepts it only when it parses to a truthy number within the page count, moving the page index just when it actually changes, and otherwise raises the error flag.
   *
   * @returns Nothing.
   */
  const onBlur = () => {
    if (pageString) {
      const pageNumber = Number(pageString);
      if (pageNumber && pageNumber <= maxPageNumber) {
        const newPageIndex = Number(pageString) - 1;
        setPageIndex((prev) => (newPageIndex !== prev.index ? { index: newPageIndex } : prev));
      } else {
        setIsError(true);
      }
    }
  };

  const { onKeyDown } = usePaginationKeyboard({
    tabZoneId: RHYTHM_EVENT_TAB_ID,
    isDisabled,
    isBlocked: isOpenModalEditStrip || isOpenModaltrip,
    isShowPagination,
    isDisabledPrev,
    isDisabledNext,
    onClickPrevious,
    onClickNext,
    pageStringRef,
    maxPageNumberRef,
    setPageIndex,
    setIsError,
    deps: [totalStrip, pageIndex, isDisabled, isOpenModalEditStrip, isOpenModaltrip],
  });

  useEmitter(
    EMITTER_CONSTANTS.RHYTHM_EVENTS_NEXT,
    () => {
      if (isShowPagination && !isDisabledNext) {
        onClickNext();
      }
    },
    [pageIndex, totalStrip],
  );

  useEmitter(
    EMITTER_CONSTANTS.RHYTHM_EVENTS_PREV,
    () => {
      if (isShowPagination && !isDisabledPrev) {
        onClickPrevious();
      }
    },
    [pageIndex, totalStrip],
  );

  useEmitter(
    EMITTER_CONSTANTS.OPEN_MODAL_EDIT_AI_STRIP,
    (msg: any) => {
      setIsOpenModalStrip(msg.value);
    },
    [],
  );

  useEffect(() => {
    setPageString(pageIndex.index + 1);
    setInputSize(String(pageIndex.index + 1).length + 1);
  }, [pageIndex]);

  useEffect(() => {
    if (maxPageNumber <= pageIndex.index) {
      setPageIndex({ index: maxPageNumber - 1 });
    }
  }, [maxPageNumber, pageIndex]);

  return {
    isShowPagination,
    isDisabledPrev,
    isDisabledNext,
    pageString,
    inputSize,
    isError,
    maxPageNumber,
    onClickPrevious,
    onClickNext,
    onChangePageString,
    onKeyDown,
    onBlur,
  };
};

export default useBookmarkPaginationContainer;
